import React, { useCallback, useState } from 'react';
import type { PersonProvider } from '@zunelo/shared';
import { usePeopleMentionCandidates } from '../../hooks/usePeopleMentionCandidates';
import { Button } from './Button';
import { RichTextComposer, type ComposerMentionCandidate } from './RichTextComposer';

const PROVIDER_LABELS: Record<PersonProvider, string> = {
  slack: 'Slack',
  jira: 'Jira',
  github: 'GitHub',
};

export interface ProviderCommentComposerProps {
  platform: Exclude<PersonProvider, 'slack'>;
  onSubmit: (markdown: string) => Promise<void>;
  /**
   * Idea-first draft generation (mirrors Slack's `Generar borrador`): the user
   * types their intent, this expands it with AI, and the result lands back in
   * the same editable composer. It never submits on its own.
   */
  onGenerateDraft?: (currentDraft: string) => Promise<string | null>;
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
  'data-testid'?: string;
}

export const ProviderCommentComposer: React.FC<ProviderCommentComposerProps> = ({
  platform,
  onSubmit,
  onGenerateDraft,
  onMentionCandidateUnavailable,
  'data-testid': testId,
}) => {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const {
    candidates,
    loading: candidatesLoading,
    error: candidatesError,
  } = usePeopleMentionCandidates(platform);
  const hasMentionableCandidates = candidates.some((candidate) => !candidate.disabled);

  const submit = useCallback(async () => {
    // Always uses the text currently visible in the composer, never a stale
    // generated value captured at some earlier render.
    const markdown = draft.trim();
    if (!markdown || busy || drafting) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await onSubmit(markdown);
      setDraft('');
      setNotice('Comentario enviado.');
    } catch (cause) {
      // Failure preserves the draft text intact so the user can retry.
      setError(cause instanceof Error ? cause.message : 'No se pudo enviar el comentario.');
    } finally {
      setBusy(false);
    }
  }, [busy, drafting, draft, onSubmit]);

  const generateDraft = useCallback(async () => {
    const idea = draft.trim();
    if (!onGenerateDraft || !idea || drafting || busy) return;
    setDrafting(true);
    setError(null);
    try {
      const generated = await onGenerateDraft(idea);
      if (generated?.trim()) setDraft(generated);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No se pudo generar el borrador con IA.');
    } finally {
      setDrafting(false);
    }
  }, [busy, drafting, draft, onGenerateDraft]);

  return (
    <section className="z-provider-comment-composer" data-testid={testId}>
      <div className="z-provider-comment-composer__header">
        <span>Comentar en {PROVIDER_LABELS[platform]}</span>
        <span>Las menciones se validan al enviar</span>
      </div>
      <RichTextComposer
        value={draft}
        onChange={setDraft}
        placeholder="Escribe un comentario…"
        minHeight={72}
        showBottomExtras
        mentionCandidates={candidates}
        onMentionCandidateUnavailable={onMentionCandidateUnavailable}
        submitOnEnter
        onSubmit={() => void submit()}
        disabled={busy || drafting}
        ariaLabel={`Comentario de ${PROVIDER_LABELS[platform]}`}
        data-testid={testId ? `${testId}-editor` : undefined}
      />
      <div className="z-provider-comment-composer__actions">
        <span className="z-provider-comment-composer__hint">
          {candidatesLoading
            ? 'Cargando Personas…'
            : candidatesError
              ? 'No se pudieron cargar las Personas.'
              : hasMentionableCandidates
                ? 'Puedes mencionar una Persona confirmada.'
                : 'Configura aliases en Personas para mencionar.'}
        </span>
        {onGenerateDraft ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid={testId ? `${testId}-generate-draft` : undefined}
            onClick={() => void generateDraft()}
            disabled={!draft.trim() || drafting || busy}
            title={
              draft.trim()
                ? 'Expandir tu idea con el tono y contexto del ítem'
                : 'Escribe primero tu idea o intención en el compositor'
            }
          >
            {drafting ? 'Generando…' : 'Generar borrador'}
          </Button>
        ) : null}
        <Button
          variant="primary"
          size="sm"
          onClick={() => void submit()}
          disabled={!draft.trim() || busy || drafting}
          loading={busy}
        >
          Enviar comentario
        </Button>
      </div>
      {error ? (
        <p className="z-provider-comment-composer__error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="z-provider-comment-composer__notice" role="status">
          {notice}
        </p>
      ) : null}
    </section>
  );
};
